import { useEffect, useRef, useState } from 'react';

interface AccessoryPositionProps {
    x?: number;
    y?: number;
}

export const MagnifyingGlass = ({ x = -65, y = 115 }: AccessoryPositionProps) => (
    <div style={{ position: 'absolute', left: x, top: y, zIndex: 4 }}>
        <svg width="90" height="90" viewBox="0 0 55 55">
            <circle cx="22" cy="22" r="16" fill="none" stroke="#7eecd4" strokeWidth="5" />
            <line x1="34" y1="34" x2="48" y2="48" stroke="#7eecd4" strokeWidth="5" strokeLinecap="round" />
        </svg>
    </div>
);

export const Zzz = ({ x = 220, y = -15 }: AccessoryPositionProps) => (
    <div style={{ position: 'absolute', left: x, top: y, zIndex: 3 }}>
        {[
            { size: 16, dx: 0, dy: 0, delay: 0 },
            { size: 22, dx: 15, dy: -22, delay: 0.6 },
            { size: 28, dx: 32, dy: -50, delay: 1.2 },
        ].map((z, i) => (
            <div
                key={i}
                style={{
                    position: 'absolute',
                    left: z.dx,
                    top: z.dy,
                    fontSize: z.size,
                    fontWeight: 'bold',
                    color: '#7b7bf0',
                    opacity: 0.7,
                    animation: `floatUp 3s ease-in-out ${z.delay}s infinite`,
                }}
            >
                z
            </div>
        ))}
    </div>
);

interface ThinkingDotsProps extends AccessoryPositionProps {
    dotSize?: number;
    gap?: number;
    // show for 2s, then again every 10s while mounted (a Worki that keeps thinking), instead of staying
    cycle?: boolean;
}

export const ThinkingDots = ({ x = 230, y = -10, dotSize = 7, gap = 4, cycle = false }: ThinkingDotsProps) => (
    <div
        className="worki-thought-dots"
        style={{
            position: 'absolute',
            left: x,
            top: y,
            zIndex: 3,
            display: 'flex',
            gap,
            transformOrigin: 'left bottom',
            animation: cycle ? 'thoughtCycle 10s ease-in-out infinite' : 'fadeIn 0.3s ease-out',
        }}
    >
        {[0, 1, 2].map((i) => (
            <div
                key={i}
                className="worki-thought-dot"
                style={{
                    width: dotSize,
                    height: dotSize,
                    borderRadius: '50%',
                    background: '#9b9bf5',
                    animation: `dotBounce 1s ease-in-out ${i * 0.2}s infinite`,
                }}
            />
        ))}
    </div>
);

export const Sparkles = () => (
    <>
        {[
            { x: -20, y: -30, color: '#fde047' },
            { x: 250, y: -20, color: '#facc15' },
            { x: 270, y: 60, color: '#fef08a' },
            { x: -10, y: 200, color: '#eab308' },
            { x: 240, y: 180, color: '#fcd34d' },
        ].map((s, i) => (
            <div
                key={i}
                style={{
                    position: 'absolute',
                    left: s.x,
                    top: s.y,
                    zIndex: 3,
                    animation: `sparkle 1.2s ease-in-out ${i * 0.25}s infinite`,
                    fontSize: 16,
                    color: s.color,
                }}
            >
                ✦
            </div>
        ))}
    </>
);

export const SadBrows = () => (
    <div
        style={{
            position: 'absolute',
            top: '38%',
            left: '50%',
            transform: 'translateX(calc(-50% + calc(var(--mouse-x) * 6px))) translateY(calc(var(--mouse-y) * 4px))',
            zIndex: 3,
            display: 'flex',
            gap: 25,
        }}
    >
        {/* Left eyebrow - soft inverted V, inner corner high */}
        <svg width="40" height="10" viewBox="0 0 34 10" style={{ overflow: 'visible', transform: 'rotate(-25deg)' }}>
            <path d="M 0,1 Q 17,10 34,0" fill="none" stroke="#2a2a5c" strokeWidth={6} strokeLinecap="round" />
        </svg>
        {/* Right eyebrow - mirrored */}
        <svg width="40" height="10" viewBox="0 0 34 10" style={{ overflow: 'visible', transform: 'rotate(25deg)' }}>
            <path d="M 0,0 Q 17,10 34,1" fill="none" stroke="#2a2a5c" strokeWidth={6} strokeLinecap="round" />
        </svg>
    </div>
);

const TEAR_SPOTS = [
    { x: '33%', top: '57%', size: 18 },
    { x: '60%', top: '57%', size: 16 },
];

export const Tears = () => {
    const [tear, setTear] = useState<{ idx: number; key: number } | null>(null);
    const keyRef = useRef(0);

    useEffect(() => {
        let timeout: ReturnType<typeof setTimeout>;
        const schedule = () => {
            timeout = setTimeout(
                () => {
                    keyRef.current++;
                    setTear({ idx: keyRef.current % TEAR_SPOTS.length, key: keyRef.current });
                    setTimeout(() => {
                        setTear(null);
                        schedule();
                    }, 2000);
                },
                3000 + Math.random() * 5000
            );
        };
        schedule();
        return () => clearTimeout(timeout);
    }, []);

    if (!tear) return null;

    const t = TEAR_SPOTS[tear.idx];
    return (
        <svg
            key={tear.key}
            width={t.size}
            height={t.size * 1.5}
            viewBox={`0 0 ${t.size} ${t.size * 1.5}`}
            style={{
                position: 'absolute',
                top: t.top,
                left: t.x,
                zIndex: 5,
                animation: 'tearDrop 2s ease-in forwards',
                overflow: 'visible',
            }}
        >
            <path
                d={`M ${t.size / 2},0 Q ${t.size},${t.size} ${t.size / 2},${t.size * 1.4} Q 0,${t.size} ${t.size / 2},0 Z`}
                fill="rgba(120, 190, 255, 0.65)"
            />
        </svg>
    );
};

export const Blush = () => (
    <>
        <div
            style={{
                position: 'absolute',
                top: '48%',
                left: '22%',
                width: 25,
                height: 12,
                borderRadius: '50%',
                background: 'rgba(255,150,150,0.25)',
                zIndex: 2,
            }}
        />
        <div
            style={{
                position: 'absolute',
                top: '48%',
                right: '22%',
                width: 25,
                height: 12,
                borderRadius: '50%',
                background: 'rgba(255,150,150,0.25)',
                zIndex: 2,
            }}
        />
    </>
);
